import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Employee } from '@models/employee/employee.model';
import { TreeNode } from '../interfaces/tree-node.interface';

/**
 * Used in org chart when we have no image, shows First initial of First/Last names
 */
const getEmployeeInitials = (employee: Employee): string => {
    if (!employee.firstName || !employee.lastName) {
        return '?';
    }

    return employee.firstName.substring(0, 1) + employee.lastName.substring(0, 1);
};

/**
 * Convert an array of employees to an array of TreeNode objects than can be used by our OrgTree
 */
function buildD3Data(sanitizer: DomSanitizer, employees: Employee[]): TreeNode[] {
    const sanitize = (value?: string): string => sanitizer.sanitize(SecurityContext.HTML, value ?? '') ?? '';
    let nodes: TreeNode[] = employees.map((employee) => {
        return {
            id: 'node-' + employee.id,
            name: sanitize(employee.fullName),
            initials: getEmployeeInitials(employee),
            parentId: employee.reportsTo ? 'node-' + employee.reportsTo : null,
            employee: employee,
            position: sanitize(employee?.job?.title),
            avatarId: employee.avatarId,
            departmentColour: sanitize(employee?.department?.color),
        };
    });

    nodes = nodes.map((node) => {
        const noParent = !node.parentId;

        if (noParent && node.id !== 'root') {
            node.parentId = 'root';
        }
        return node;
    });

    return nodes;
}

/**
 * If an employee ReportsTo someone who is not in array, remove the ReportsTo.
 * This can happen when filtering to a department etc.
 */
function redactReportsToIfNotInArray(nodes: TreeNode[]): TreeNode[] {
    return nodes.map((node, _index, array) => {
        if (!node.parentId && node.id !== 'root') {
            node.parentId = 'root';
            return node;
        }

        const reportsToIsInArray = Boolean(array.find((otherNode) => node.parentId === otherNode.id));

        if ((!reportsToIsInArray || node.id === node.parentId) && node.id !== 'root') {
            node.parentId = 'root';
        }

        return node;
    });
}

export { buildD3Data, getEmployeeInitials, redactReportsToIfNotInArray };
